import { useState } from "preact/hooks";
import type { Field as FieldDef } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { instantToLocal, localToRfc3339, localToZoned, parseZoned } from "../../lib/datetime.ts";
import { isDuration } from "../../lib/duration.ts";
import { formatDuration, formatZoned } from "../../lib/format.ts";
import { useValueText } from "../../lib/textState.ts";
import { timeZone, zoneOptions } from "../../lib/zone.ts";
import { Input, Select } from "../../ui/form.tsx";

type OnChange = (v: unknown) => void;

interface Props {
  field: FieldDef;
  value: unknown;
  onChange: OnChange;
  label: string | undefined;
}

const SHORT = 16; // "YYYY-MM-DDTHH:mm"
const ZERO_SECONDS = ":00";

const str = (v: unknown) => (typeof v === "string" ? v : "");

/** datetime-local shows seconds only when they aren't zero. */
const forInput = (local: string) => (local.slice(SHORT) === ZERO_SECONDS ? local.slice(0, SHORT) : local);

/** An exact moment, picked as a wall time in the viewer's zone and stored as RFC 3339 with that offset. */
function InstantInput({ value, onChange, label }: Props) {
  const tz = timeZone.value;
  const local = typeof value === "string" && !Number.isNaN(Date.parse(value)) ? instantToLocal(value, tz) : "";
  return (
    <div class="flex items-center gap-2">
      <Input
        type="datetime-local"
        aria-label={label}
        value={local}
        onValue={(v) => onChange(v ? (localToRfc3339(v, tz) ?? undefined) : undefined)}
      />
      <span class="text-xs whitespace-nowrap text-zinc-500">{tz}</span>
    </div>
  );
}

/** A wall time in a chosen zone (RFC 9557). The zone defaults to the field's (else the space's), else the viewer's;
 *  changing the zone keeps the wall time. */
function ZonedInput({ field, value, onChange, label }: Props) {
  const parsed = parseZoned(str(value));
  // The value's zone (changed elsewhere too); without one, the one picked last (for the wall time to come).
  const [picked, setZone] = useState(parsed?.zone ?? field.time_zone ?? timeZone.value);
  const zone = parsed?.zone ?? picked;
  const local = parsed ? forInput(parsed.local) : "";
  const emit = (l: string, z: string) => onChange(l ? (localToZoned(l, z) ?? undefined) : undefined);
  const here = formatZoned(str(value))?.here;
  return (
    <div class="flex flex-col gap-1">
      <div class="flex flex-wrap gap-2">
        <Input
          type="datetime-local"
          class="w-auto flex-1"
          aria-label={label}
          value={local}
          onValue={(l) => emit(l, zone)}
        />
        <Select
          class="w-auto flex-1"
          aria-label={t("fields.timeZone")}
          value={zone}
          onValue={(z) => {
            setZone(z);
            emit(local, z);
          }}
          options={zoneOptions(zone)}
        />
      </div>
      {here ? <span class="text-xs text-zinc-500">{t("fields.yourTime", { time: here })}</span> : null}
    </div>
  );
}

/** ISO 8601 duration text, validated, with a humanized preview. */
function DurationInput({ value, onChange, label }: Props) {
  const [text, setText] = useValueText(
    str(value),
    (v) => v,
    (s, v) => s === v,
  );
  const ok = text === "" || isDuration(text);
  return (
    <div class="flex items-center gap-3">
      <Input
        aria-label={label}
        value={text}
        placeholder="P1DT2H"
        spellcheck={false}
        aria-invalid={!ok || undefined}
        onValue={(v) => {
          setText(v.trim());
          if (v.trim() === "" || isDuration(v.trim())) {
            onChange(v.trim() || undefined);
          }
        }}
      />
      <span class="text-xs whitespace-nowrap text-zinc-500">
        {ok ? formatDuration(text) : t("fields.durationHint")}
      </span>
    </div>
  );
}

/** Editors for the temporal field types (Temporal / RFC 9557 shapes; see openapi.json FieldType). */
export function TemporalInput(p: Props) {
  const set = (v: string) => p.onChange(v || undefined);
  switch (p.field.type) {
    case "time":
      return <Input type="time" aria-label={p.label} value={str(p.value)} onValue={set} />;
    case "local_datetime":
      return (
        <Input
          type="datetime-local"
          aria-label={p.label}
          value={forInput(str(p.value))}
          onValue={(v) => set(v.length === SHORT ? `${v}${ZERO_SECONDS}` : v)}
        />
      );
    case "datetime":
      return <ZonedInput {...p} />;
    case "duration":
      return <DurationInput {...p} />;
    case "timezone":
      return (
        <Select
          aria-label={p.label}
          value={str(p.value)}
          onValue={set}
          options={[{ value: "", label: "—" }, ...zoneOptions()]}
        />
      );
    default:
      return <InstantInput {...p} />;
  }
}
